// src/features/form/EntityFormsSettings.tsx
// Admin escape hatch for the entity-embedded RPCs (AttachForm/DetachForm/
// GetEntityForm/BatchGetEntityForms). These act on entities owned by other
// modules (event, job opening, …) — per the entity-embedded placement rule
// they belong in that entity's own rail, out of scope for this pass (would
// touch another module's page). This tab keeps them reachable until a host
// page wires them in.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert, Badge, Button, Input, Textarea } from '@react/components';
import { useFormForm } from '@react/hooks';
import type { Form } from '@core/types/form';
import { errMessage } from './helpers';

export function EntityFormsSettings() {
  const { t } = useI18n();
  const store = useFormForm();

  const [targetType, setTargetType] = useState('event');
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [entityForm, setEntityForm] = useState<Form | null | 'none'>(null);

  const [batchUrns, setBatchUrns] = useState('');
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState<string | null>(null);

  const lookup = async () => {
    if (!targetId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const form = await store.GetEntityForm(targetType.trim(), targetId.trim());
      setEntityForm(form ?? 'none');
    } catch (e) {
      setEntityForm('none');
      setError(errMessage(e, t('form.entityForms.loadError')));
    } finally {
      setBusy(false);
    }
  };

  const attach = async () => {
    if (!targetId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await store.AttachForm(targetType.trim(), targetId.trim(), {
        target: { type_id: '', type_name: targetType.trim(), id: targetId.trim(), platform_id: '', tenant_id: '', product_id: '' },
      });
      await lookup();
    } catch (e) {
      setError(errMessage(e, t('form.entityForms.attachError')));
    } finally {
      setBusy(false);
    }
  };

  const detach = async () => {
    if (!targetId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await store.DetachForm(targetType.trim(), targetId.trim(), {
        target: { type_id: '', type_name: targetType.trim(), id: targetId.trim(), platform_id: '', tenant_id: '', product_id: '' },
        archive_submissions: true,
      });
      setEntityForm('none');
    } catch (e) {
      setError(errMessage(e, t('form.entityForms.detachError')));
    } finally {
      setBusy(false);
    }
  };

  const batchLookup = async () => {
    const urns = batchUrns.split('\n').map((s) => s.trim()).filter(Boolean);
    if (urns.length === 0) return;
    setBatchBusy(true);
    setBatchResult(null);
    setError(null);
    try {
      const res = await store.BatchGetEntityForms({ host_urns: urns, scope: undefined });
      setBatchResult(t('form.entityForms.batchResult', { count: Object.keys(res.entries ?? {}).length }));
    } catch (e) {
      setError(errMessage(e, t('form.entityForms.batchError')));
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-muted-foreground">{t('form.entityForms.hint')}</p>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-2 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('form.entityForms.singleTitle')}</span>
        <div className="flex flex-wrap items-end gap-2">
          <Input label={t('form.policies.entityType')} value={targetType} onChange={(e) => setTargetType(e.target.value)} fullWidth={false} className="w-36" />
          <Input label={t('form.policies.entityId')} value={targetId} onChange={(e) => setTargetId(e.target.value)} fullWidth={false} className="w-72" />
          <Button size="sm" variant="secondary" onClick={lookup} isLoading={busy} disabled={!targetId.trim()}>
            {t('form.entityForms.lookup')}
          </Button>
          <Button size="sm" onClick={attach} isLoading={busy} disabled={!targetId.trim()}>
            {t('form.entityForms.attach')}
          </Button>
          <Button size="sm" variant="danger" onClick={detach} isLoading={busy} disabled={!targetId.trim()}>
            {t('form.entityForms.detach')}
          </Button>
        </div>
        {entityForm === 'none' && <p className="text-sm text-muted-foreground">{t('form.entityForms.none')}</p>}
        {entityForm && entityForm !== 'none' && (
          <p className="text-sm text-foreground">
            {entityForm.name} <Badge variant="default">{entityForm.status}</Badge>
          </p>
        )}
      </div>

      <div className="space-y-2 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('form.entityForms.batchTitle')}</span>
        <Textarea
          value={batchUrns}
          onChange={(e) => setBatchUrns(e.target.value)}
          placeholder={t('form.entityForms.batchPlaceholder')}
          rows={3}
        />
        <Button size="sm" variant="secondary" onClick={batchLookup} isLoading={batchBusy} disabled={!batchUrns.trim()}>
          {t('form.entityForms.batchLookup')}
        </Button>
        {batchResult && <p className="text-sm text-foreground">{batchResult}</p>}
      </div>
    </div>
  );
}
